import { useState } from "react";
import { aiModels, type AiModelConfig } from "@/mocks/aiAdmin";

export default function ModelYonetimi() {
  const [models, setModels] = useState<AiModelConfig[]>(aiModels);
  const [editing, setEditing] = useState<string | null>(null);

  function toggleActive(id: string) {
    setModels((prev) => prev.map((m) => (m.id === id ? { ...m, active: !m.active } : m)));
  }

  function updateField(id: string, field: keyof AiModelConfig, value: string | number | boolean) {
    setModels((prev) => prev.map((m) => (m.id === id ? { ...m, [field]: value } : m)));
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-foreground-500">
        Her modeli açıp kapatabilir, fallback modelini, timeout ve token ayarlarını düzenleyebilirsiniz.
      </p>

      <div className="space-y-3">
        {models.map((m) => (
          <div
            key={m.id}
            className="rounded-lg border border-background-200 bg-background-50 p-5"
          >
            <div className="flex flex-wrap items-start gap-4">
              <span className="w-11 h-11 flex items-center justify-center rounded-md bg-primary-100 text-primary-700 shrink-0">
                <i className={`${m.icon} text-xl`} aria-hidden="true"></i>
              </span>

              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="font-heading font-semibold text-base text-foreground-950">{m.name}</h3>
                  <span className="text-xs font-medium text-foreground-500 whitespace-nowrap">{m.provider}</span>
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                      m.active ? "bg-emerald-100 text-emerald-700" : "bg-background-200 text-foreground-500"
                    }`}
                  >
                    {m.active ? "Aktif" : "Pasif"}
                  </span>
                </div>
                <p className="mt-1 text-sm text-foreground-500">{m.role}</p>
                <p className="mt-1 text-sm text-foreground-600">{m.description}</p>

                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                  <div>
                    <span className="block text-xs text-foreground-400">Model ID</span>
                    <span className="text-foreground-700 break-all">{m.modelId}</span>
                  </div>
                  <div>
                    <span className="block text-xs text-foreground-400">Fallback</span>
                    <span className="text-foreground-700">{m.fallback}</span>
                  </div>
                  <div>
                    <span className="block text-xs text-foreground-400">Timeout</span>
                    <span className="text-foreground-700">{(m.timeoutMs / 1000).toFixed(0)} sn</span>
                  </div>
                  <div>
                    <span className="block text-xs text-foreground-400">Max token / Sıcaklık</span>
                    <span className="text-foreground-700">{m.maxTokens} / {m.temperature}</span>
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={() => toggleActive(m.id)}
                    className={`px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                      m.active
                        ? "bg-background-200 text-foreground-700 hover:bg-background-300"
                        : "bg-accent-500 text-background-50 hover:bg-accent-600"
                    }`}
                  >
                    {m.active ? "Devre Dışı Bırak" : "Etkinleştir"}
                  </button>

                  <button
                    type="button"
                    onClick={() => setEditing(editing === m.id ? null : m.id)}
                    className="px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap text-foreground-700 border border-background-200 hover:border-accent-300 transition-colors"
                  >
                    {editing === m.id ? "Kapat" : "Ayarları Düzenle"}
                  </button>
                </div>

                {editing === m.id && (
                  <div className="mt-4 rounded-md bg-background-100 p-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <label className="block">
                      <span className="text-xs text-foreground-500">Temperature</span>
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        max="2"
                        value={m.temperature}
                        onChange={(e) => updateField(m.id, "temperature", parseFloat(e.target.value) || 0)}
                        className="mt-1 w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 focus:outline-none focus:border-accent-400"
                      />
                    </label>
                    <label className="block">
                      <span className="text-xs text-foreground-500">Max Token</span>
                      <input
                        type="number"
                        min="1"
                        value={m.maxTokens}
                        onChange={(e) => updateField(m.id, "maxTokens", parseInt(e.target.value, 10) || 0)}
                        className="mt-1 w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 focus:outline-none focus:border-accent-400"
                      />
                    </label>
                    <label className="block">
                      <span className="text-xs text-foreground-500">Timeout (sn)</span>
                      <input
                        type="number"
                        min="1"
                        value={m.timeoutMs / 1000}
                        onChange={(e) => updateField(m.id, "timeoutMs", (parseInt(e.target.value, 10) || 1) * 1000)}
                        className="mt-1 w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 focus:outline-none focus:border-accent-400"
                      />
                    </label>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}